import React, { JSX } from "react";
import { 
  headingStyles, 
  bodyStyles, 
  labelStyles, 
  captionStyles,
  displayStyles,
  linkStyles,
  paragraphStyles 
} from "@/styles/typography";

// Type definitions
type HeadingVariant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type BodyVariant = "large" | "base" | "small" | "xs";
type DisplayVariant = "large" | "base" | "small";
type TextColor = "primary" | "secondary" | "tertiary" | "inverse" | "error" | "success" | "accent";

// Color mappings
const colorClasses: Record<TextColor, string> = {
  primary: "text-content-primary",
  secondary: "text-content-secondary",
  tertiary: "text-content-tertiary",
  inverse: "text-content-inverse",
  error: "text-error",
  success: "text-success-dark",
  accent: "text-accent",
};

// Base props
interface BaseTypographyProps {
  children: React.ReactNode;
  className?: string;
  color?: TextColor;
}

/**
 * Unified Typography component
 * Compatible with Material Tailwind API: <Typography variant="h1">...</Typography>
 * 
 * Supports:
 * - Heading variants: h1, h2, h3, h4, h5, h6
 * - Body text: text-lg, text-base, text-sm, text-xs
 * - Paragraph: paragraph, lead
 * - Small: small
 */
export type TypographyVariant = 
  | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" 
  | "text-lg" | "text-base" | "text-sm" | "text-xs"
  | "paragraph" | "lead" | "small"
  // Material UI compatibility aliases
  | "body1" | "body2";

export interface TypographyProps extends BaseTypographyProps {
  variant?: TypographyVariant;
  as?: keyof JSX.IntrinsicElements;
}

const Typography: React.FC<TypographyProps> = ({
  variant = "text-base",
  as,
  children,
  className = "",
  color,
}) => {
  // Determine the element type based on variant
  let Component: keyof JSX.IntrinsicElements = as || "p";
  let baseStyle = "";
  
  // Handle heading variants
  if (variant.startsWith("h") && variant.length === 2) {
    Component = as || (variant as keyof JSX.IntrinsicElements);
    baseStyle = headingStyles[variant as HeadingVariant] || "";
  }
  // Handle text variants
  else if (variant.startsWith("text-")) {
    const size = variant.replace("text-", "") as BodyVariant;
    Component = as || "p";
    baseStyle = bodyStyles[size] || bodyStyles.base;
  }
  // Handle paragraph/lead
  else if (variant === "paragraph" || variant === "lead") {
    Component = as || "p";
    baseStyle = paragraphStyles[variant === "lead" ? "lead" : "base"] || "";
  }
  // Handle small
  else if (variant === "small") {
    Component = as || "span";
    baseStyle = bodyStyles.small || "";
  }
  // Handle Material UI compatibility (body1 = base, body2 = small)
  else if (variant === "body1") {
    Component = as || "p";
    baseStyle = bodyStyles.base;
  }
  else if (variant === "body2") {
    Component = as || "p";
    baseStyle = bodyStyles.small;
  }
  // Default
  else {
    baseStyle = bodyStyles.base;
  }
  
  const colorStyle = color ? colorClasses[color] : "";
  
  return (
    <Component className={`${baseStyle} ${colorStyle} ${className}`.trim()}>
      {children}
    </Component>
  );
};

/**
 * Heading component
 * Replaces MT Typography variant="h1-h6"
 */
export interface HeadingProps extends BaseTypographyProps {
  as?: HeadingVariant;
}

export const Heading: React.FC<HeadingProps> = ({
  as: Component = "h2",
  children,
  className = "",
  color,
}) => {
  const baseStyle = headingStyles[Component];
  const colorStyle = color ? colorClasses[color] : "";
  
  return (
    <Component className={`${baseStyle} ${colorStyle} ${className}`.trim()}>
      {children}
    </Component>
  );
};

/**
 * Text component
 * Replaces MT Typography for body text
 */
export interface TextProps extends BaseTypographyProps {
  variant?: BodyVariant;
  as?: keyof JSX.IntrinsicElements;
}

export const Text: React.FC<TextProps> = ({
  variant = "base",
  as: Component = "p",
  children,
  className = "",
  color,
}) => {
  const baseStyle = bodyStyles[variant];
  const colorStyle = color ? colorClasses[color] : "";
  
  return (
    <Component className={`${baseStyle} ${colorStyle} ${className}`.trim()}>
      {children}
    </Component>
  );
};

/**
 * Label component
 * For form labels and similar
 */
export interface LabelProps extends BaseTypographyProps {
  variant?: "large" | "base" | "small";
  htmlFor?: string;
  required?: boolean;
}

export const Label: React.FC<LabelProps> = ({
  variant = "base",
  children,
  className = "",
  color,
  htmlFor,
  required,
}) => {
  const baseStyle = labelStyles[variant];
  const colorStyle = color ? colorClasses[color] : "";
  
  return (
    <label htmlFor={htmlFor} className={`${baseStyle} ${colorStyle} ${className}`.trim()}>
      {children}
      {required && <span className="text-error ml-1">*</span>}
    </label>
  );
};

/**
 * Caption component
 * For helper text, captions, etc.
 */
export interface CaptionProps extends BaseTypographyProps {
  variant?: "base" | "error" | "success";
}

export const Caption: React.FC<CaptionProps> = ({
  variant = "base",
  children,
  className = "",
}) => {
  const baseStyle = captionStyles[variant];
  
  return (
    <span className={`${baseStyle} ${className}`.trim()}>
      {children}
    </span>
  );
};

/**
 * Display component
 * For large hero text
 */
export interface DisplayProps extends BaseTypographyProps {
  variant?: DisplayVariant;
  as?: "h1" | "h2" | "h3" | "div" | "span";
}

export const Display: React.FC<DisplayProps> = ({
  variant = "base",
  as: Component = "h1",
  children,
  className = "",
  color,
}) => {
  const baseStyle = displayStyles[variant];
  const colorStyle = color ? colorClasses[color] : "";
  
  return (
    <Component className={`${baseStyle} ${colorStyle} ${className}`.trim()}>
      {children}
    </Component>
  );
};

/**
 * Paragraph component
 * Optimized for readable paragraphs
 */
export interface ParagraphProps extends BaseTypographyProps {
  variant?: "lead" | "base" | "small";
}

export const Paragraph: React.FC<ParagraphProps> = ({
  variant = "base",
  children,
  className = "",
  color,
}) => {
  const baseStyle = paragraphStyles[variant];
  const colorStyle = color ? colorClasses[color] : "";
  
  return (
    <p className={`${baseStyle} ${colorStyle} ${className}`.trim()}>
      {children}
    </p>
  );
};

/**
 * Link text component
 * For text links
 */
export interface LinkTextProps extends BaseTypographyProps {
  variant?: "base" | "subtle" | "muted";
  href?: string;
  onClick?: () => void;
}

export const LinkText: React.FC<LinkTextProps> = ({
  variant = "base",
  children,
  className = "",
  href,
  onClick,
}) => {
  const baseStyle = linkStyles[variant];
  
  if (href) {
    return (
      <a href={href} className={`${baseStyle} ${className}`.trim()}>
        {children}
      </a>
    );
  }
  
  return (
    <button type="button" onClick={onClick} className={`${baseStyle} ${className}`.trim()}>
      {children}
    </button>
  );
};

// Display names for debugging
Typography.displayName = "Typography";
Heading.displayName = "Heading";
Text.displayName = "Text";
Label.displayName = "Label";
Caption.displayName = "Caption";
LinkText.displayName = "LinkText";

export { Typography };
export default Typography;
